import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import { DataGridPro, type GridColDef, type GridRowModesModel } from '@mui/x-data-grid-pro'
import type { TextProcessorPattern, TextProcessorPatternPatch } from 'api/integrationsErrors/types'
import { useState } from 'react'

interface IntegrationErrorProps {
  textProcessors: TextProcessorPattern[]
  isLoading: boolean
  handlePatchTextProcessor: (textProcessor: TextProcessorPatternPatch) => void
  handleDeleteTextProcessor: (id: string) => void
}

export const IntegrationError = ({
  textProcessors,
  isLoading,
  handlePatchTextProcessor,
  handleDeleteTextProcessor,
}: IntegrationErrorProps) => {
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel)
  }

  const columns: GridColDef<TextProcessorPattern>[] = [
    {
      field: 'pattern',
      headerName: 'Pattern',
      flex: 1.25,
      type: 'string',
      editable: true,
    },
    {
      field: 'matchText',
      headerName: 'Match Text',
      flex: 1,
      type: 'string',
      editable: true,
    },
    {
      field: 'isActive',
      headerName: 'Activated',
      type: 'boolean',
      editable: true,
      renderCell: (params) => (
        <Checkbox
          aria-label="active rule"
          checked={params.value}
          onChange={(event) => {
            handlePatchTextProcessor({
              id: params.row.id,
              isActive: event.target.checked,
            })
          }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Delete pattern',
      type: 'actions',
      renderCell: (params) => (
        <Button
          aria-label="delete pattern"
          size="small"
          variant="contained"
          color="error"
          onClick={() => {
            handleDeleteTextProcessor(params.row.id)
          }}
        >
          Delete
        </Button>
      ),
    },
  ]

  return (
    <Box sx={{ p: 3, height: 600, width: '100%' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Integration errors
      </Typography>

      <DataGridPro
        rows={textProcessors}
        columns={columns}
        loading={isLoading}
        editMode="row"
        rowModesModel={rowModesModel}
        processRowUpdate={(newRow) => {
          handlePatchTextProcessor({
            id: newRow.id,
            pattern: newRow.pattern,
            matchText: newRow.matchText,
            isActive: newRow.isActive,
          })

          return newRow
        }}
        onRowModesModelChange={handleRowModesModelChange}
        disableRowSelectionOnClick
        slots={{
          noRowsOverlay: () => (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="body2" color="text.secondary">
                No integration errors found
              </Typography>
            </Box>
          ),
        }}
      />
    </Box>
  )
}
